import { useState } from 'react';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { rolesDe } from '../../logica/acceso';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { fechaCorta, useCarga } from './carga';
import { canalDe, consejoEnvio, estadoInvitado, lineaInvitado, nombreConAcompanantes, PERMISOS_PORTAL, sinProtocolo, ultimoEnvio } from './logica';
import type { DatosPortal } from './PortalRrpp';

/** Invitados del RRPP (10.12): de 8 en 8 con «Anterior / Siguiente» sin totales (G121); `?invitado=` abre la ficha. */
export function Invitados({ props, datos, onCopiar }: { props: PropsPantalla; datos: DatosPortal; onCopiar: (t: string) => void }) {
    const { negocio, busqueda, ruta, navegar, movil, membresia, nombreNegocio } = props;
    const permisos = usePermisos();
    const [evento, setEvento] = useState('');
    const pag = usePaginacionCursor();
    const ver = permisos.puede(PERMISOS_PORTAL.invitados);
    const carga = useCarga(ver ? `invitados|${negocio}|${evento}|${pag.cursor ?? ''}` : null, () => datos.ops.listGuests(negocio, { event_id: evento === '' ? undefined : evento, cursor: pag.cursor ?? undefined, limit: 8 }));
    const abierto = busqueda.get('invitado');

    if (!ver) {
        return <EstadoSinPermiso seccion="Invitados" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const abrir = (id: string | null) => {
        navegar(id === null ? `${ruta}?seccion=invitados` : `${ruta}?seccion=invitados&invitado=${encodeURIComponent(id)}`, true);
    };
    const lista = carga.datos;
    return (
        <div className="rv-ca-apartado">
            {datos.eventos.length > 1 && (
                <Desplegable
                    etiqueta="Evento"
                    valor={evento}
                    opciones={[{ valor: '', texto: 'Todos tus eventos' }, ...datos.eventos.map((e) => ({ valor: e.id, texto: e.name ?? '' }))]}
                    onChange={(v) => {
                        setEvento(v);
                        pag.reiniciar();
                    }}
                />
            )}
            {carga.error !== null && lista === null ? (
                <EstadoError seccion="tus invitados" codigoSoporte={estadoDeError(carga.error).codigoSoporte} onReintentar={carga.recargar} movil={movil} />
            ) : lista === null ? (
                <div className="rv-rp-esqueleto" aria-busy="true" />
            ) : lista.data.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa"><Icono nombre="users" /></span>
                    <strong>Aún no tienes invitados</strong>
                    <span>Apunta a alguien o comparte tus enlaces: quien se apunte con ellos saldrá aquí.</span>
                </div>
            ) : (
                <>
                    <div className="rv-ca-lista" aria-label="Tus invitados">
                        {lista.data.map((i) => {
                            const estado = estadoInvitado(i);
                            return (
                                <div key={i.id} className="rv-ca-fila-caja">
                                    <div className="rv-ca-fila rv-ca-fila--abre" role="button" tabIndex={0} onClick={() => { abrir(i.id); }} onKeyDown={(e) => { if (e.key === 'Enter') { abrir(i.id); } }}>
                                        <span className="rv-ca-fila__texto">
                                            <strong>{nombreConAcompanantes(i)}</strong>
                                            <span>{`${lineaInvitado(i)} · ${datos.nombreLista(i.guest_list_id)}`}</span>
                                        </span>
                                        {estado.chip === null ? <span className="rv-rp-gris">{estado.texto}</span> : <Chip estado={estado.chip}>{estado.texto}</Chip>}
                                        <Icono nombre="chevron-right" className="rv-ca-chevron" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
                    <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="info" /><span>Ves cómo le llega el pase, no su contacto completo. «Apuntado»: falta que te escriba por Instagram con su enlace personal.</span></p>
                </>
            )}
            {abierto !== null && <FichaInvitado key={abierto} props={props} datos={datos} id={abierto} onCerrar={() => { abrir(null); }} onCambio={carga.recargar} onCopiar={onCopiar} />}
        </div>
    );
}

function FichaInvitado({ props, datos, id, onCerrar, onCambio, onCopiar }: { props: PropsPantalla; datos: DatosPortal; id: string; onCerrar: () => void; onCambio: () => void; onCopiar: (t: string) => void }) {
    const { negocio, movil, soloLectura } = props;
    const ops = datos.ops;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const ficha = useCarga(`invitado|${id}`, () => ops.getGuest(negocio, id));
    const invitado = ficha.datos?.datos ?? null;
    const verEnvios = permisos.puede(PERMISOS_PORTAL.registro);
    const envios = useCarga(verEnvios ? `envios|${id}` : null, () => ops.listDeliveries(negocio, { deliverable_type: 'invitado', deliverable_id: id, limit: 1 }));
    const deInstagram = invitado !== null && invitado.contact_channel === 'instagram' && invitado.status === 'active' && permisos.puede(PERMISOS_PORTAL.enlaces);
    const personal = useCarga(deInstagram ? `personal|${id}` : null, () => ops.getGuestPersonalLink(negocio, id));
    const gestionar = permisos.puede(PERMISOS_PORTAL.apuntar) && !soloLectura;

    const [nombre, setNombre] = useState<string | null>(null);
    const [acompanantes, setAcompanantes] = useState<number | null>(null);
    const [guardando, setGuardando] = useState(false);
    const [reenviando, setReenviando] = useState(false);
    const [retirar, setRetirar] = useState<string | null>(null);

    if (ficha.error !== null && invitado === null) {
        const e = ficha.error;
        return (
            <PanelLateral abierto onCerrar={onCerrar} movil={movil} tamano="ficha" titulo="Invitado">
                <p className="rv-ca-nota"><Icono nombre="circle-alert" /><span>{e instanceof ErrorApi && e.estado === 404 ? 'Este invitado ya no está en tus listas.' : (avisoBreveDeError(e) ?? 'No hemos podido cargar el invitado.')}</span></p>
            </PanelLateral>
        );
    }
    if (invitado === null || ficha.datos === null) {
        return (
            <PanelLateral abierto onCerrar={onCerrar} movil={movil} tamano="ficha" titulo="Invitado">
                <div className="rv-rp-esqueleto" aria-busy="true" />
            </PanelLateral>
        );
    }
    const version = ficha.datos.version ?? `"${String(invitado.version)}"`;
    const lista = Object.values(datos.listas).flat().find((l) => l.id === invitado.guest_list_id);
    const max = lista?.max_companions ?? invitado.companions;
    const abiertaLista = lista === undefined || lista.status === 'open';
    const activo = invitado.status === 'active';
    const editable = gestionar && activo && abiertaLista;
    const nombreActual = nombre ?? invitado.display_name;
    const acompActual = acompanantes ?? invitado.companions;
    const cambiado = nombreActual.trim() !== invitado.display_name || acompActual !== invitado.companions;
    const estado = estadoInvitado(invitado);
    const canal = canalDe(invitado);
    const envio = envios.datos?.data[0] ?? null;

    const guardar = () => {
        setGuardando(true);
        const cambio: { name?: string; companions?: number } = {};
        if (nombreActual.trim() !== invitado.display_name) {
            cambio.name = nombreActual.trim();
        }
        if (acompActual !== invitado.companions) {
            cambio.companions = acompActual;
        }
        ops.updateGuest(negocio, id, cambio, version)
            .then((r) => {
                ficha.poner(r);
                setNombre(null);
                setAcompanantes(null);
                onCambio();
                avisar({ tipo: 'ok', texto: 'Guardado' });
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 412) {
                    ficha.recargar();
                    avisar({ tipo: 'error', texto: 'Alguien lo cambió antes. Te enseñamos lo último.' });
                } else if (e instanceof ErrorApi && e.estado === 409) {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No queda sitio en la lista o ya está cerrada.' });
                } else {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido guardar.' });
                }
            })
            .finally(() => { setGuardando(false); });
    };
    const reenviar = () => {
        if (envio === null) {
            return;
        }
        setReenviando(true);
        ops.resendDelivery(negocio, envio.id, null)
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Pase reenviado' });
                envios.recargar();
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido reenviar.' }); })
            .finally(() => { setReenviando(false); });
    };
    const confirmarRetirada = () => {
        const motivo = (retirar ?? '').trim();
        ops.withdrawGuest(negocio, id, motivo)
            .then((g) => {
                setRetirar(null);
                ficha.poner({ datos: g, version: null });
                onCambio();
                avisar({ tipo: 'ok', texto: 'Retirado de la lista' });
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido retirar.' }); });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            eyebrow={`${datos.nombreEvento(invitado.event_id)} · ${datos.nombreLista(invitado.guest_list_id)}`}
            titulo={nombreConAcompanantes(invitado)}
            chip={estado.texto}
            chipEstado={estado.chip ?? 'neutral'}
            accion={editable ? 'Guardar' : ''}
            inactiva={!cambiado || nombreActual.trim().length < 2}
            ocupado={guardando}
            onAccion={guardar}
        >
            <div className="rv-ca-cuerpo">
                {invitado.status === 'withdrawn' && invitado.withdrawn_by_guest && (
                    <div className="rv-ca-caja"><div className="rv-ca-caja__fila"><Icono nombre="user-minus" /><span>Salió él mismo de la lista con el enlace de su aviso de privacidad.</span></div></div>
                )}
                {deInstagram && personal.datos !== null && personal.datos.instagram_url != null && (
                    <div className="rv-ca-caja">
                        <strong>Su enlace personal</strong>
                        <code className="rv-rp-codigo">{sinProtocolo(personal.datos.instagram_url)}</code>
                        <Boton tipo="secundario" icono="copy" onClick={() => { onCopiar(personal.datos?.instagram_url ?? ''); }}>Copiar enlace</Boton>
                        <span>Mándaselo por Instagram desde tu cuenta. Cuando te escriba con él, le llega el pase.</span>
                    </div>
                )}
                {editable ? (
                    <Campo etiqueta="Nombre y apellidos" valor={nombreActual} onChange={setNombre} />
                ) : (
                    <div className="rv-ca-dato"><span>Nombre</span><strong>{invitado.display_name}</strong></div>
                )}
                <div className="rv-ca-dato"><span>Cómo le llega</span><strong>{canal.texto}</strong></div>
                <div className="rv-ca-dato"><span>Cómo se apuntó</span><strong>{`${lineaInvitado({ ...invitado, withdrawn_by_guest: false }).split(' · ').slice(1).join(' · ')} · ${fechaCorta(invitado.created_at)}`}</strong></div>
                <div className="rv-ca-dato">
                    <span>{`Acompañantes${editable ? ` · hasta ${String(max)}` : ''}`}</span>
                    {editable && max > 0 ? (
                        <span className="rv-rp-contador">
                            <button type="button" aria-label="Uno menos" disabled={acompActual <= 0} onClick={() => { setAcompanantes(acompActual - 1); }}>−</button>
                            <strong aria-live="polite">{acompActual}</strong>
                            <button type="button" aria-label="Uno más" disabled={acompActual >= max} onClick={() => { setAcompanantes(acompActual + 1); }}>+</button>
                        </span>
                    ) : (
                        <strong>{invitado.companions}</strong>
                    )}
                </div>
                {verEnvios && (
                    <div className="rv-ca-bloque">
                        <div className="rv-ca-bloque__cab"><strong>Su último envío</strong></div>
                        {envio === null ? (
                            <span className="rv-rp-gris">{envios.datos === null ? 'Cargando…' : 'Todavía no se le ha enviado nada.'}</span>
                        ) : (
                            <>
                                <div className="rv-ca-dato"><span>{fechaCorta(envio.created_at)}</span><strong className={ultimoEnvio(envio).problema ? 'rv-ca-aviso-texto' : undefined}>{ultimoEnvio(envio).texto}</strong></div>
                                {consejoEnvio(envio) !== null && <div className="rv-ca-caja rv-ca-caja--aviso"><span>{consejoEnvio(envio)}</span></div>}
                                {activo && permisos.puede(PERMISOS_PORTAL.reenviar) && (
                                    <Boton tipo="secundario" icono="send" ocupado={reenviando} ocupadoTexto="Reenviando…" onClick={reenviar}>Reenviar el pase</Boton>
                                )}
                            </>
                        )}
                    </div>
                )}
                {editable && (
                    <div className="rv-ca-bloque">
                        <span className="rv-rp-gris">Si lo retiras, su pase y su enlace dejan de valer y se libera su plaza.</span>
                        <Boton tipo="destructivo" icono="user-minus" onClick={() => { setRetirar(''); }}>Retirar de la lista</Boton>
                    </div>
                )}
            </div>
            <Ventana
                abierta={retirar !== null}
                titulo="¿Retirar de la lista?"
                texto="Su pase deja de valer y se libera su plaza."
                cancelar="Cancelar"
                accion="Retirar"
                accionIcono="user-minus"
                inactiva={(retirar ?? '').trim().length < 10}
                movil={movil}
                onAccion={confirmarRetirada}
                onCerrar={() => { setRetirar(null); }}
            >
                <Campo etiqueta="Motivo" valor={retirar ?? ''} onChange={setRetirar} ayuda="Al menos 10 letras. Queda registrado." />
            </Ventana>
        </PanelLateral>
    );
}
